import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserPlus, faUserEdit, faUserMinus, faUserSlash, faUserCheck } from '@fortawesome/free-solid-svg-icons';
import { useEffect } from 'react';
import Table from '@shared/components/Table';
import DeleteModal from '@shared/components/DeleteModal';
import PersonForm from './PersonForm';
import { usePerson } from '@modules/staff/person/forms/personBasicInfo/hooks/usePerson';

export default function Person() {
    const { t } = useTranslation();
    const {
        data,
        error,
        loading,
        personData,
        selectedData,
        modalState,
        pageNumber,
        pageSize,
        sortBy,
        sortDir,
        totalElements,
        totalPages,
        searchQuery,
        searchType,
        searchOptions,
        searchParams,
        handleOpenModal,
        handleCloseModal,
        handleAddCloseModal,
        handleEditPerson,
        handleConfirmDelete,
        handleDeactivatePerson,
        handleActivatePerson,
        handlePersonSearchClick,
        setSearchQuery,
        setSearchType,
        handleSort,
        setPageSize,
        setpageNumber,
        setSelectedData,
        selectedPersonName,
        fetchPersons,
        personService,
        setTotalElements,
        setTotalPages,
        selectedActive,
        setSelectedActive,
    } = usePerson();

    useEffect(() => {
        fetchPersons(personService.getPersons);
    }, [pageNumber, pageSize, sortBy, sortDir, selectedActive]);

    const columns = [
        { key: 'id', label: t('users.table.headers.id'), sortable: true },
        { key: 'idn', label: t('users.table.headers.idn'), sortable: true },
        { key: 'name', label: t('users.table.headers.firstName'), sortable: true },
        { key: 'fathersName', label: t('users.table.headers.middleName'), sortable: true },
        { key: 'surname', label: t('users.table.headers.familyName'), sortable: true },
        { key: 'nationality', label: t('users.table.headers.citizenship'), sortable: true },
        { key: 'dateOfBirth', label: t('users.table.headers.dateOfBirth'), sortable: true }
    ];

    const actionButtons = (
        <>
            <button
                className="icon-button"
                onClick={() => handleOpenModal('add')}
                title={t('common.add')}
            >
                <FontAwesomeIcon icon={faUserPlus} />
            </button>

            <button
                className={`icon-button ${!selectedData ? 'disabled-link' : ''}`}
                onClick={handleEditPerson}
                title={t('common.edit')}
            >
                <FontAwesomeIcon icon={faUserEdit} />
            </button>

            <button
                className={`icon-button ${!selectedData ? 'disabled-link' : ''}`}
                onClick={() => handleOpenModal('delete')}
                title={t('common.remove')}
            >
                <FontAwesomeIcon icon={faUserMinus} />
            </button>

            <button
                className={`icon-button ${!selectedData ? 'disabled-link' : ''}`}
                onClick={handleDeactivatePerson}
                title={t('common.deactivate')}
            >
                <FontAwesomeIcon icon={faUserSlash} />
            </button>

            <button
                className={`icon-button ${!selectedData ? 'disabled-link' : ''}`}
                onClick={handleActivatePerson}
                title={t('common.activate')}
            >
                <FontAwesomeIcon icon={faUserCheck} />
            </button>
        </>
    );

    const handleCheckboxChange = (index) => {
        const person = data[index];
        setSelectedData(selectedData === person.id ? null : person.id);
    };

    return (
        <>
            <Table
                // Data and state
                data={data}
                loading={loading}
                error={error}
                selectedData={selectedData}
                pageNumber={pageNumber}
                pageSize={pageSize}
                sortBy={sortBy}
                sortDir={sortDir}
                totalElements={totalElements}
                totalPages={totalPages}
                searchQuery={searchQuery}
                searchType={searchType}
                searchOptions={searchOptions}
                
                // Column configuration
                columns={columns}
                
                // Title and actions
                title={t('users.titleHrRegister')}
                actionButtons={actionButtons}
                
                // Handlers
                handleSort={handleSort}
                handleCheckboxChange={handleCheckboxChange}
                handleSearchClick={handlePersonSearchClick}
                setSearchQuery={setSearchQuery}
                setSearchType={setSearchType}
                setPageSize={setPageSize}
                setpageNumber={setpageNumber}
                getData={personService.getPersons}
                showActiveSelect={true}
                selectedActive={selectedActive}
                setSelectedActive={setSelectedActive}
                
                // Custom renderers
                renderCell={(item, column) => {
                    if (column.key === 'id') {
                        return '';
                    }
                    if (column.key === 'idn') {
                        return item.idn.idn;
                    }
                    if (column.key === 'nationality') {
                        // Handle nationality display - fix [object Object] issue
                        const nationality = item.nationality;
                        if (!nationality) return '';
                        if (typeof nationality === 'string') {
                            // Check if it's the problematic "[object Object]" string
                            if (nationality === '[object Object]' || nationality === 'null' || nationality === 'undefined' || nationality.trim() === '') {
                                return t('users.table.headers.bgNationality'); // Default nationality
                            }
                            return nationality;
                        }
                        if (typeof nationality === 'object' && nationality !== null) {
                            return nationality.label || nationality.value || t('persons.nationality.bulgarian');
                        }
                        return t('users.table.headers.bgNationality'); // Fallback
                    }
                    if (column.key === 'dateOfBirth') {
                        // Format date of birth in Bulgarian format
                        if (item.dateOfBirth) {
                            const date = new Date(item.dateOfBirth);
                            if (!isNaN(date.getTime())) {
                                const day = String(date.getDate()).padStart(2, '0');
                                const month = String(date.getMonth() + 1).padStart(2, '0');
                                const year = date.getFullYear();
                                return `${day}.${month}.${year}`;
                            }
                        }
                        return item.dateOfBirth || '';
                    }
                    return item[column.key];
                }}
            />

            {modalState.visible && modalState.type === 'delete' && (
                <DeleteModal
                    onClose={handleCloseModal}
                    onConfirm={handleConfirmDelete}
                    name={selectedPersonName}
                    entityType="persons"
                />
            )}
            {modalState.visible && (modalState.type === 'add' || modalState.type === 'edit') && (
                <PersonForm
                    onClose={handleAddCloseModal}
                    person={personData}
                    selectedData={selectedData}
                    fetchData={fetchPersons}
                />
            )}
        </>
    );
}
